import { useState, type FormEvent } from 'react'
import { useCreateObjective } from '../hooks/useObjectives'
import { createObjectiveSchema } from '../types'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'
import { useCourses } from '@/features/courses/hooks/useCourses'
import { useTranslation } from 'react-i18next'

interface CreateObjectiveModalProps {
  isOpen: boolean
  onClose: () => void
}

export function CreateObjectiveModal({ isOpen, onClose }: CreateObjectiveModalProps) {
  const { t } = useTranslation()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [targetDate, setTargetDate] = useState('')
  const [courseId, setCourseId] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const createMutation = useCreateObjective()
  const { data: coursesData } = useCourses({})
  const courses = coursesData?.data ?? []

  function resetForm() {
    setTitle('')
    setDescription('')
    setTargetDate('')
    setCourseId('')
    setErrors({})
  }

  function handleClose() {
    resetForm()
    onClose()
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setErrors({})

    const result = createObjectiveSchema.safeParse({
      title,
      description: description || undefined,
      target_date: targetDate || undefined,
      course_id: courseId || undefined,
    })

    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      for (const issue of result.error.issues) {
        const key = issue.path[0]?.toString() ?? 'root'
        fieldErrors[key] = issue.message
      }
      setErrors(fieldErrors)
      return
    }

    try {
      await createMutation.mutateAsync(result.data)
      resetForm()
      onClose()
    } catch {
      setErrors({ root: t('objectives.errors.generic') })
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-objective-title"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-5 flex items-center justify-between">
          <h2
            id="create-objective-title"
            className="text-lg font-semibold text-neutral-900"
          >
            {t('objectives.modal.title')}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label={t('objectives.modal.close')}
            className="rounded-md p-1.5 text-neutral-600 hover:bg-surface-100 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-5 w-5"
              aria-hidden="true"
            >
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>

        {errors.root && (
          <Alert variant="error" className="mb-4">
            {errors.root}
          </Alert>
        )}

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <div>
            <label
              htmlFor="obj-title"
              className="mb-1 block text-sm font-medium text-neutral-700"
            >
              {t('objectives.modal.field_title')}{' '}
              <span className="text-mindeia-error" aria-hidden="true">*</span>
            </label>
            <input
              id="obj-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('objectives.modal.title_placeholder')}
              className="w-full rounded-lg border border-surface-200 px-3 py-2 text-sm text-neutral-900 placeholder-neutral-600 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            {errors.title && (
              <p className="mt-1 text-xs text-mindeia-error">{t(errors.title)}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="obj-description"
              className="mb-1 block text-sm font-medium text-neutral-700"
            >
              {t('objectives.modal.description')}
            </label>
            <textarea
              id="obj-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('objectives.modal.description_placeholder')}
              rows={3}
              className="w-full rounded-lg border border-surface-200 px-3 py-2 text-sm text-neutral-900 placeholder-neutral-600 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div>
            <label
              htmlFor="obj-date"
              className="mb-1 block text-sm font-medium text-neutral-700"
            >
              {t('objectives.modal.target_date')}
            </label>
            <input
              id="obj-date"
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className="w-full rounded-lg border border-surface-200 px-3 py-2 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          {courses.length > 0 && (
            <div>
              <label
                htmlFor="obj-course"
                className="mb-1 block text-sm font-medium text-neutral-700"
              >
                {t('objectives.modal.course')}
              </label>
              <select
                id="obj-course"
                value={courseId}
                onChange={(e) => setCourseId(e.target.value)}
                className="w-full rounded-lg border border-surface-200 px-3 py-2 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="">{t('objectives.modal.course_none')}</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" isLoading={createMutation.isPending}>
              {t('objectives.modal.submit')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
